import { z } from "zod";
import { attachmentTypes } from "../modules/prompt-editor/constants/attachmentTypes";

/**
 * The single source of truth for how an attachment is shaped, shared by prompt
 * messages, chat messages, and the "attachment" variable type.
 *
 * An attachment is either a concrete uploaded file or a reference to a file
 * variable whose files are resolved at run time — chat resolves them per
 * request, the playground freezes them when a job is drafted.
 *
 * `supabase/functions/_shared/schemas/AttachmentSchemas.ts` is a deliberate
 * mirror of this file for the Deno edge functions, which cannot import from
 * `webapp/`. Change the two together.
 */

/** A reference to a file variable; its files live in the variable's `items`. */
export const VariableAttachmentSchema = z.object({
  type: z.literal(attachmentTypes.VARIABLE),
  name: z.string().trim().nonempty(),
});

/**
 * An uploaded file in the `resource-assets` bucket. `name` is the generated
 * storage key (uuid-prefixed); the user-visible filename is
 * `meta.original_name`, and the MIME type is `meta.type`.
 */
export const FileAttachmentSchema = z.object({
  id: z.string(),
  type: z.literal(attachmentTypes.FILE),
  name: z.string().trim().nonempty(),
  path: z.string(),
  full_path: z.string(),
  meta: z.object({
    original_name: z.string().trim().nonempty(),
    size: z.number(),
    type: z.string(),
  }),
});

export const AttachmentSchema = VariableAttachmentSchema.or(FileAttachmentSchema);

export type VariableAttachment = z.infer<typeof VariableAttachmentSchema>;
export type FileAttachment = z.infer<typeof FileAttachmentSchema>;
export type Attachment = z.infer<typeof AttachmentSchema>;
